import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { Toaster as Sonner, type ToasterProps, toast } from 'sonner'
import { useTheme } from '@/theme'

/**
 * Single place to configure every toast in the app.
 *
 * Exactly one `<Toaster>` is mounted (main.tsx, alongside the providers) —
 * a second instance renders every toast twice, since sonner's `toast()`
 * publishes to a module-level observer that each Toaster subscribes to
 * independently. Mount points pass no options: position, duration, offset
 * and sizing are decided here so they hold app-wide.
 *
 * Toasts drop in top-centre just below the top bar and run for
 * TOAST_DURATION_MS. They are wider and set in a larger type than sonner's
 * defaults because they carry outcomes an analyst must actually read
 * ("already fully on the graph", "12 entities added") rather than ambient
 * chrome. Centre rather than right: the right edge is where the agent panel
 * lives, and a toast there covers its header controls for the whole
 * duration.
 */

/** How long a toast stays up. Per-call `duration` still wins — pass it for
 *  anything genuinely transient. */
export const TOAST_DURATION_MS = 4000

/** Clears the top bar (`h-12` + 1px bottom border, see layout/TopBar.tsx)
 *  plus a gutter, so a toast never sits on top of the breadcrumb or the
 *  workspace actions. Bump this if the bar's height changes. */
const TOAST_TOP_OFFSET = '61px'

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      position="top-center"
      offset={{ top: TOAST_TOP_OFFSET }}
      mobileOffset={{ top: TOAST_TOP_OFFSET }}
      closeButton
      icons={{
        success: <CircleCheckIcon className="size-5" />,
        info: <InfoIcon className="size-5" />,
        warning: <TriangleAlertIcon className="size-5" />,
        error: <OctagonXIcon className="size-5" />,
        loading: <Loader2Icon className="size-5 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
          // Sonner's default is 356px; these carry sentence-length outcomes.
          '--width': '420px',
        } as React.CSSProperties
      }
      toastOptions={{
        duration: TOAST_DURATION_MS,
        classNames: {
          // `!` throughout: sonner ships its own `[data-sonner-toast]` rules
          // at the same specificity as a utility class, so plain Tailwind
          // would win or lose on stylesheet order.
          toast: '!gap-3 !p-4 !text-sm',
          title: '!font-medium !leading-snug',
          // Sonner parks the dismiss button in a corner overhanging the
          // toast; only its colours need to follow the theme.
          closeButton:
            '!bg-popover !border-border !text-muted-foreground hover:!text-foreground hover:!bg-muted',
          success:
            'group-[.toaster]:!bg-success-surface group-[.toaster]:!border-success-border group-[.toaster]:!text-success-foreground',
          error:
            'group-[.toaster]:!bg-destructive-surface group-[.toaster]:!border-destructive-border group-[.toaster]:!text-destructive-foreground',
          warning:
            'group-[.toaster]:!bg-warning-surface group-[.toaster]:!border-warning-border group-[.toaster]:!text-warning-foreground',
          info: 'group-[.toaster]:!bg-info-surface group-[.toaster]:!border-info-border group-[.toaster]:!text-info-foreground',
          description: 'group-[.toaster]:!text-current/70',
        },
      }}
      {...props}
    />
  )
}

export { Toaster, toast }
